import * as React from "react";
import { Track } from "livekit-client";
import { MediaDeviceMenu } from "@livekit/components-react";
// import { DisconnectButton } from '@livekit/components-react';
import { TrackToggle } from "@livekit/components-react";
// import { ChatIcon, GearIcon, LeaveIcon } from '@livekit/components-react';
// import { ChatToggle } from '@livekit/components-react';
import {
  useLocalParticipantPermissions,
  usePersistentUserChoices,
} from "@livekit/components-react";
import { supportsScreenSharing } from "@livekit/components-core";
import { StartMediaButton } from "@livekit/components-react";

export function useMediaQuery(query: string): boolean {
  const getMatches = (query: string): boolean => {
    // Prevents SSR issues
    if (typeof window !== "undefined") {
      return window.matchMedia(query).matches;
    }
    return false;
  };

  const [matches, setMatches] = React.useState<boolean>(getMatches(query));

  function handleChange() {
    setMatches(getMatches(query));
  }

  React.useEffect(() => {
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    handleChange();

    // Listen matchMedia
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener("change", handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener("change", handleChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return matches;
}

export type ControlBarControls = {
  microphone?: boolean;
  camera?: boolean;
  chat?: boolean;
  screenShare?: boolean;
  leave?: boolean;
  settings?: boolean;
};
/** @public */
export interface CustomControlBarProps
  extends React.HTMLAttributes<HTMLDivElement> {
  onDeviceError?: (error: { source: Track.Source; error: Error }) => void;
  variation?: "minimal" | "verbose" | "textOnly";
  controls?: ControlBarControls;
  saveUserChoices?: boolean;
  showCameraControl?: boolean; // New custom prop
  showMicrophoneControl?: boolean; // New custom prop
  showChatControl?: boolean; // New custom prop
}

/**
 * Custom Control Bar for controlling media devices (camera, microphone, screen share), chat, and leave options.
 */
export function CustomControlBar({
  variation = "verbose",
  controls = {},
  saveUserChoices = true,
  showCameraControl = true, // Default true for camera
  showMicrophoneControl = true, // Default true for microphone
  showChatControl = true, // Default true for chat
  onDeviceError,
  ...props
}: CustomControlBarProps) {
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const isTooLittleSpace = useMediaQuery("(max-width: 760px)");

  // Determine variation based on screen size and chat state
  const defaultVariation = isTooLittleSpace ? "minimal" : "verbose";
  const activeVariation = variation || defaultVariation;

  const visibleControls = { leave: true, ...controls };

  const localPermissions = useLocalParticipantPermissions();

  // Set visibility for controls based on permissions
  if (!localPermissions) {
    visibleControls.camera = false;
    visibleControls.chat = false;
    visibleControls.microphone = false;
    visibleControls.screenShare = false;
  } else {
    visibleControls.camera ??= localPermissions.canPublish;
    visibleControls.microphone ??= localPermissions.canPublish;
    visibleControls.screenShare ??= localPermissions.canPublish;
    visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
  }

  const showIcon =
    activeVariation === "minimal" || activeVariation === "verbose";
  const showText =
    activeVariation === "textOnly" || activeVariation === "verbose";

  const browserSupportsScreenSharing = supportsScreenSharing();

  const [isScreenShareEnabled, setIsScreenShareEnabled] = React.useState(false);

  const onScreenShareChange = React.useCallback((enabled: boolean) => {
    setIsScreenShareEnabled(enabled);
  }, []);

  const htmlProps = {
    className: "lk-control-bar",
    ...props, // Spread the other props passed into the component
  };

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: !saveUserChoices });

  const microphoneOnChange = React.useCallback(
    (enabled: boolean, isUserInitiated: boolean) =>
      isUserInitiated ? saveAudioInputEnabled(enabled) : null,
    [saveAudioInputEnabled],
  );

  const cameraOnChange = React.useCallback(
    (enabled: boolean, isUserInitiated: boolean) =>
      isUserInitiated ? saveVideoInputEnabled(enabled) : null,
    [saveVideoInputEnabled],
  );

  return (
    <div {...htmlProps}>
      {/* Microphome removed from the Custom Control Bar */}
      {/* {showMicrophoneControl && visibleControls.microphone && (
        <div className="lk-button-group">
          <TrackToggle
            source={Track.Source.Microphone}
            showIcon={showIcon}
            onChange={microphoneOnChange}
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Microphone, error })}
          >
            {showText && 'Microphone'}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="audioinput"
              onActiveDeviceChange={(_kind, deviceId) => saveAudioInputDeviceId(deviceId ?? '')}
            />
          </div>
        </div>
      )} */}

      {showCameraControl && visibleControls.camera && (
        <div className="lk-button-group">
          <TrackToggle
            source={Track.Source.Camera}
            showIcon={showIcon}
            onChange={cameraOnChange}
            onDeviceError={(error) =>
              onDeviceError?.({ source: Track.Source.Camera, error })
            }
          >
            {showText && "Camera"}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="videoinput"
              onActiveDeviceChange={(_kind, deviceId) =>
                saveVideoInputDeviceId(deviceId ?? "")
              }
            />
          </div>
        </div>
      )}

      <StartMediaButton />
    </div>
  );
}
